/** SUNTRONIX ONE — Visual lifecycle timeline (orders & devices). RFQ → … → Redeploy/Buyback/Recycle. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, Clock, Hourglass, AlertTriangle, Siren, Circle, ExternalLink, Paperclip, Loader2, UserRound, CalendarClock } from "lucide-react";

export const STATUS = {
  completed: { label: "Completed", dot: "bg-emerald-500 border-emerald-500 text-white", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check },
  on_track: { label: "On Track", dot: "bg-sky-500 border-sky-500 text-white", chip: "bg-sky-50 text-sky-700 border-sky-200", Icon: Clock },
  pending: { label: "Pending", dot: "bg-amber-400 border-amber-400 text-white", chip: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass },
  waiting_external: { label: "Waiting External", dot: "bg-violet-500 border-violet-500 text-white", chip: "bg-violet-50 text-violet-700 border-violet-200", Icon: Hourglass },
  delayed: { label: "Delayed", dot: "bg-orange-500 border-orange-500 text-white", chip: "bg-orange-50 text-orange-700 border-orange-200", Icon: AlertTriangle },
  critical: { label: "Critical", dot: "bg-rose-600 border-rose-600 text-white", chip: "bg-rose-50 text-rose-700 border-rose-200", Icon: Siren },
  not_started: { label: "Not started", dot: "bg-white border-slate-300 text-slate-300", chip: "bg-slate-50 text-slate-400 border-slate-200", Icon: Circle },
};
const dt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const dOnly = (s) => (s ? String(s).slice(0, 10) : "—");
const tOnly = (s) => (s && String(s).length > 10 ? String(s).slice(11, 16) : "—");

export function StatusChip({ status, testId }) {
  const m = STATUS[status] || STATUS.not_started;
  return <span data-testid={testId} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold border rounded px-1.5 py-0.5 ${m.chip}`}><m.Icon className="h-3 w-3" />{m.label}</span>;
}

export default function LifecycleTimeline({ kind, id, compact = false, onLoaded }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/lifecycle/${kind}/${id}`); setData(data); setErr(null); onLoaded?.(data); }
    catch (e) { setErr(e.response?.data?.detail || "Lifecycle unavailable"); }
  }, [kind, id]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-xs text-slate-400" data-testid="lifecycle-error">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Building lifecycle…</div>;
  const done = data.stages.filter((s) => s.status === "completed").length;

  return (
    <div data-testid={`lifecycle-${kind}`} className="space-y-3">
      {!compact && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="font-mono">{done}/{data.stages.length} stages complete</span>
          {data.current_stage && <span>· current: <b className="text-slate-800">{data.stages.find((s) => s.key === data.current_stage)?.label}</b></span>}
          <div className="ml-auto flex flex-wrap gap-1.5">{Object.entries(STATUS).filter(([k]) => k !== "not_started").map(([k, m]) => <span key={k} className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${m.dot.split(" ")[0]}`} />{m.label}</span>)}</div>
        </div>
      )}
      <div className="overflow-x-auto pb-2 -mx-1 px-1">
        <ol className="flex items-start min-w-max" data-testid="lifecycle-stages">
          {data.stages.map((s, i) => {
            const m = STATUS[s.status] || STATUS.not_started;
            const nextDone = data.stages[i + 1] && data.stages[i + 1].status !== "not_started";
            return (
              <li key={s.key} className="flex items-start">
                <button onClick={() => setOpen(s)} data-testid={`lifecycle-stage-${s.key}`} className="group flex flex-col items-center w-[92px] sm:w-[104px] text-center">
                  <span className={`h-8 w-8 rounded-full border-2 flex items-center justify-center transition-transform group-hover:scale-110 ${m.dot} ${s.key === data.current_stage ? "ring-4 ring-sky-100" : ""}`}><m.Icon className="h-4 w-4" /></span>
                  <span className={`mt-1.5 text-[11px] leading-tight font-medium ${s.status === "not_started" ? "text-slate-400" : "text-slate-800"}`}>{s.label}</span>
                  {!compact && <span className="text-[10px] text-slate-400 font-mono mt-0.5">{s.at ? dOnly(s.at) : "—"}</span>}
                  {!compact && s.sla_state && s.sla_state !== "ok" && <span className={`text-[9px] uppercase font-semibold mt-0.5 ${s.sla_state === "breached" ? "text-rose-600" : "text-amber-600"}`}>SLA {s.sla_state.replace("_", " ")}</span>}
                </button>
                {i < data.stages.length - 1 && <span className={`mt-4 h-0.5 w-4 sm:w-6 -mx-3 ${nextDone || s.status === "completed" ? "bg-emerald-400" : "bg-slate-200"}`} />}
              </li>
            );
          })}
        </ol>
      </div>
      <StageSheet stage={open} data={data} onClose={() => setOpen(null)} onChanged={load} kind={kind} id={id} />
    </div>
  );
}

function StageSheet({ stage, data, onClose, onChanged, kind, id }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user && !user.master_company_id;
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(""); setReason(stage?.pending_reason || ""); }, [stage]);
  if (!stage) return null;
  const m = STATUS[stage.status] || STATUS.not_started;
  const save = async (body) => {
    setSaving(true);
    try { await api.post(`/lifecycle/${kind}/${data.record.id}/stage/${stage.key}/note`, body); toast.success("Saved"); onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const Row = ({ k, v, testId }) => <div className="flex gap-3 py-1.5 border-b border-slate-50 text-[13px]"><span className="w-28 shrink-0 text-slate-500">{k}</span><span className="text-slate-900 min-w-0 break-words" data-testid={testId}>{v ?? <span className="text-slate-300">—</span>}</span></div>;
  return (
    <Sheet open={!!stage} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lifecycle-stage-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base"><span className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${m.dot}`}><m.Icon className="h-3.5 w-3.5" /></span>{stage.label}<StatusChip status={stage.status} testId="stage-status-chip" /></SheetTitle>
        </SheetHeader>
        <div className="mt-3">
          <Row k="Status" v={m.label} />
          <Row k="Owner" v={stage.owner ? <span className="inline-flex items-center gap-1"><UserRound className="h-3.5 w-3.5 text-slate-400" />{stage.owner}</span> : null} testId="stage-owner" />
          <Row k="Date" v={dOnly(stage.at)} testId="stage-date" />
          <Row k="Time" v={tOnly(stage.at)} />
          <Row k="SLA" v={stage.sla_due ? <span className={stage.sla_state === "breached" ? "text-rose-700" : stage.sla_state === "at_risk" ? "text-amber-700" : "text-emerald-700"}><CalendarClock className="h-3.5 w-3.5 inline mr-1" />due {dt(stage.sla_due)} · {stage.sla_state?.replace("_", " ")}</span> : (stage.status === "completed" ? "Met" : null)} testId="stage-sla" />
          <Row k="Record" v={stage.record ? (stage.record_route ? <button onClick={() => { onClose(); navigate(stage.record_route); }} className="text-sky-700 hover:underline inline-flex items-center gap-1" data-testid="stage-record-link">{stage.record}<ExternalLink className="h-3 w-3" /></button> : stage.record) : null} />
          <Row k="Detail" v={stage.detail} />
          <Row k="Pending reason" v={stage.pending_reason ? <span className="text-amber-800">{stage.pending_reason}</span> : null} testId="stage-pending-reason" />
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><Paperclip className="h-3 w-3" /> Attachments ({stage.attachments?.length || 0})</div>
          {(stage.attachments || []).length === 0 && <div className="text-xs text-slate-400">No attachments at this stage.</div>}
          {(stage.attachments || []).map((a, i) => a.url
            ? <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block text-[13px] text-sky-700 hover:underline py-0.5" data-testid={`stage-attachment-${i}`}>{a.name || "Attachment"}</a>
            : <div key={i} className="text-[13px] text-slate-700 py-0.5">{a.name}</div>)}
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Comments ({stage.comments?.length || 0})</div>
          {(stage.comments || []).length === 0 && <div className="text-xs text-slate-400">No comments yet.</div>}
          {(stage.comments || []).map((c) => <div key={c.id} className="text-[13px] rounded bg-slate-50 border border-slate-100 px-2.5 py-1.5 mb-1" data-testid={`stage-comment-${c.id}`}><span className="font-medium text-slate-700">{(c.by || "").split("@")[0]}</span><span className="text-slate-400 font-mono text-[10px] ml-2">{dt(c.at)}</span><div className="text-slate-700">{c.text}</div></div>)}
          {isStaff && (
            <div className="mt-2 space-y-2">
              <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment on this stage…" data-testid="stage-comment-input" />
              <div className="flex gap-2">
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Pending reason (optional)" data-testid="stage-reason-input" className="h-9" />
                <Button disabled={saving || (!text.trim() && !reason.trim())} data-testid="stage-note-save" onClick={() => save({ text: text.trim() || null, pending_reason: reason.trim() || null })}>Save</Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Activity history ({stage.activities?.length || 0})</div>
          <ol className="relative border-l border-slate-200 ml-1.5 pl-4 space-y-2" data-testid="stage-activity">
            {(stage.activities || []).length === 0 && <li className="text-xs text-slate-400">No activity recorded for this stage.</li>}
            {(stage.activities || []).map((a, i) => (
              <li key={i} className="relative text-[13px]">
                <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-white border-2 border-slate-300" />
                <div className="text-slate-900">{a.title}</div>
                <div className="text-[11px] text-slate-500">{dt(a.at)}{a.actor ? ` · ${String(a.actor).split("@")[0]}` : ""}{a.detail ? ` · ${a.detail}` : ""}</div>
              </li>
            ))}
          </ol>
        </div>
      </SheetContent>
    </Sheet>
  );
}
